import { FC, PropsWithChildren } from 'react'

import { styled } from 'styled-components'

import AlertDialog from 'core/components/lib/AlertDialog'
import ConfirmDialog from 'core/components/lib/ConfirmDialog'
import IconButton from 'core/components/lib/IconButton'
import { useHasPermission } from 'core/hooks'
import { variables } from 'core/styles'
import { Permission } from 'core/types'

type SensitiveContentProps = PropsWithChildren<{
  className?: string
  value:
    | {
        sensitiveBorrowerOnly?: boolean | null
        sensitiveData?: boolean | null
        sensitive?: boolean | null
      }
    | undefined
  permissions?: Permission | Array<Permission>
}>

export const SensitiveContent: FC<SensitiveContentProps> = ({
  className,
  value,
  permissions = 'interaction:read.sensitive',
  children,
}) => {
  const canReadSensitive = useHasPermission(permissions)

  return (
    value?.sensitiveBorrowerOnly ?
      <Sensitive className={className}>
        "Account Credential" interactions are never displayed in the CRM because they contain information used to verify
        borrower details
      </Sensitive>
    : (value?.sensitive || value?.sensitiveData) && !canReadSensitive ?
      <Sensitive className={className}>
        Content is locked. Only those with access, like a manager, can unlock this content.
      </Sensitive>
    : children
  )
}

type LockButtonProps = {
  color?: string
  lockedColor?: string
  disabled?: boolean
  value: boolean | undefined | null
  onChange: (value: boolean) => Promise<unknown>
  permissions: Permission | Array<Permission>
  desc?: string
}

const LockButton: FC<LockButtonProps> = ({
  disabled = false,
  value = true,
  onChange,
  permissions,
  desc = 'content',
}) => {
  const hasPermissions = useHasPermission(permissions)

  return (
    !value ?
      <ConfirmDialog
        content={`Are you sure you want to lock this ${desc}? Only those with access, like a manager, will be able to view, unlock, or otherwise interact with it.`}
        onConfirm={() => onChange(true)}
        title={`Lock ${desc}`}
      >
        <IconButton checked={false} disabled={disabled} name='lock_open' outlined />
      </ConfirmDialog>
    : hasPermissions ? <IconButton checked disabled={disabled} name='lock' onClick={() => onChange(false)} />
    : <AlertDialog
        content={`You do not have the required permissions to unlock this ${desc}`}
        title={`Cannot unlock ${desc}`}
      >
        <IconButton checked disabled={disabled} name='lock' />
      </AlertDialog>
  )
}

export default LockButton

const Sensitive = styled.div`
  padding: 16px 24px;
  text-align: center;
  color: ${variables.colorBlack60};
  font-size: 14px;
  font-style: italic;
`
